/**
 * Agents v3 — the inline viewer for a findings-mode run's HTML output (the
 * Weekly KPI Check scorecard, later custom agents). A modal with a SANDBOXED
 * iframe: the document is fetched as a string under the normal API auth and
 * rendered via `srcdoc` with `sandbox=""` — no scripts, no forms, no
 * same-origin access — so agent-authored HTML can never touch the app.
 *
 * The report contract already requires inline CSS/SVG and no external
 * resources, which is exactly what an empty sandbox permits.
 */
import { useEffect, useRef, useState } from 'react';
import { getRunOutputHtml } from '../../../lib/api/client-reports/client-agents-api';

interface Props {
  runId: string;
  title: string;
  onClose: () => void;
}

const AgentOutputViewer = ({ runId, title, onClose }: Props) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    getRunOutputHtml(runId)
      .then((out) => {
        if (!cancelled) setHtml(out.html);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(
          err?.status === 404
            ? 'This run has no output to show.'
            : err?.message || 'Failed to load the output.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className='agentsHub__overlay agentsHub__overlay--center'
      role='dialog'
      aria-modal='true'
      aria-label={title}
      onClick={onClose}
    >
      <div className='agentsHub__viewer' onClick={(e) => e.stopPropagation()}>
        <div className='agentsHub__viewer-head'>
          <h3>{title}</h3>
          <button
            ref={closeRef}
            type='button'
            className='agentsHub__close'
            aria-label='Close'
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {error ? (
          <p className='agentsHub__muted agentsHub__viewer-state'>{error}</p>
        ) : html === null ? (
          <p className='agentsHub__muted agentsHub__viewer-state'>Loading…</p>
        ) : (
          <iframe
            className='agentsHub__viewer-frame'
            title={title}
            sandbox=''
            srcDoc={html}
            data-testid='agent-output-frame'
          />
        )}
      </div>
    </div>
  );
};

export default AgentOutputViewer;
